import "./list-patient.scss";

import { GetPatientDto } from "@api/api";
import { GetPatientModel } from "@factory/model/query-model-patient";
import { useQyGetPatient } from "@factory/query/patient.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { Outlet, useNavigate } from "react-router-dom";

import { FilterPatient } from "../filter-patient/filter-patient";
import { useFilterPatientContext } from "../filter-patient/filter-patient.context";

export function ListPatient() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterPatientContext();
  const { formatDateLocal } = useDateTimeContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: patientResponse,
    isLoading,
    isError,
    error,
  } = useQyGetPatient(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetPatientModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };
  const handleVisit = (item: GetPatientModel) => {
    const name = `${item.first_name} ${item.last_name}`;
    navigate(`visit?patient=${item.code}&name=${name}`);
  };
  const colName = (cell: GetPatientDto) => {
    return (
      <div>
        {cell.first_name.toUpperCase()} {cell?.middle_name?.toUpperCase()}{" "}
        {cell.last_name.toUpperCase()}
      </div>
    );
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        {/* <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        /> */}
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterPatient />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{patientResponse?.count}</b>
      </h5>

      <DataTable
        value={patientResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column body={(cell) => colName(cell)} header="Name" sortable></Column>
        <Column
          header="Birthday"
          sortable
          body={(rowData) =>
            rowData.birthday ? formatDateLocal(rowData.birthday) : "N/A"
          }
        />
        <Column field="gender" header="Gender" sortable></Column>
        <Column field="address" header="Address" sortable></Column>
        <Column field="mobile_number" header="Mobile Number" sortable></Column>
        <Column field="email" header="Email" sortable></Column>
        <Column
          field="is_taking_medication"
          body={(cell) => (
            <Tag
              value={cell.is_taking_medication ? "Yes" : "No"}
              severity={cell.is_taking_medication ? "success" : "info"}
            />
          )}
          header="Taking medication"
          sortable
        ></Column>
        <Column
          field="other_medical_conditions"
          header="Other Problems"
          sortable
        ></Column>
        <Column
          header="Actions"
          body={(cell) => (
            <Button
              label="Visit"
              outlined
              onClick={() => handleVisit(cell)}
              severity="secondary"
            />
          )}
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={patientResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListPatient" className="patient">
      <HeaderContent title={<h1>Patient</h1>}>
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
        <Outlet />
      </div>
    </div>
  );
}

export default ListPatient;
